import { useState, useCallback } from 'react'
import { Building2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import { DepartmentSummaryTable } from './components/DepartmentSummaryTable'
import { CommissionFilterBar } from './components/CommissionFilterBar'
import { CommissionTable } from './components/CommissionTable'
import { useDepartmentCommissions } from '@/hooks/useCommissions'
import type { DepartmentCommissionListParams } from '@/types/commission'

export function DepartmentCommissionsPage() {
  const [params, setParams] = useState<DepartmentCommissionListParams>({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
  })

  const { data, isLoading, error } = useDepartmentCommissions(params)

  const handleParamsChange = useCallback((newParams: Partial<DepartmentCommissionListParams>) => {
    setParams((prev) => ({
      ...prev,
      ...newParams,
      page: 1,
    }))
  }, [])

  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }))
  }, [])

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setParams((prev) => ({ ...prev, page: 1, page_size: pageSize }))
  }, [])

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">部门佣金</h1>
          <p className="text-muted-foreground">查看部门佣金汇总和明细</p>
        </div>
        <div className="flex items-center gap-2">
          {/* TODO: 实现导出功能 */}
          <Button variant="outline" size="sm" disabled>
            <Building2 className="mr-1.5 h-4 w-4" />
            导出数据
          </Button>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : '加载数据失败，请稍后重试'}
          </AlertDescription>
        </Alert>
      )}

      {/* 部门汇总 */}
      <DepartmentSummaryTable
        departmentSummary={data?.department_summary || []}
        isLoading={isLoading && !data}
      />

      {/* 筛选栏 */}
      <CommissionFilterBar
        params={params}
        onParamsChange={handleParamsChange}
        showScopeFilter
      />

      {/* 佣金列表 */}
      <CommissionTable
        commissions={data?.list || []}
        isLoading={isLoading}
        showUserInfo={true}
        showDepartmentInfo={true}
      />

      {/* 分页 */}
      <ListPagination
        page={params.page}
        pageSize={params.page_size}
        total={data?.total || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  )
}
